import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';
import { MermaidBlock } from './MermaidBlock';

interface MarkdownPreviewProps {
  content: string;
  className?: string;
}

/**
 * Check if a URL is external (different host or explicit external protocol)
 * Story 233: External links open in new tab with semantic contrast
 */
function isExternalUrl(href: string | undefined): boolean {
  if (!href) return false;
  // Relative URLs are internal
  if (href.startsWith('/') || href.startsWith('#')) return false;
  // Explicit external protocols
  if (href.startsWith('http://') || href.startsWith('https://')) {
    try {
      const url = new URL(href);
      return url.hostname !== window.location.hostname;
    } catch {
      return false;
    }
  }
  return false;
}

// Custom renderers for mermaid diagrams, headings, and styled tables
const components: Components = {
  // Story 233: External links open in new tab with semantic contrast
  a({ href, children }) {
    const isExternal = isExternalUrl(href);

    return (
      <a
        href={href}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        style={{
          color: 'var(--ceetrix-green, #22c55e)',
          textDecoration: 'underline',
          textDecorationStyle: isExternal ? 'dashed' : 'solid',
          textUnderlineOffset: '2px',
        }}
      >
        {children}
        {isExternal && (
          <span
            style={{
              display: 'inline-block',
              marginLeft: '3px',
              fontSize: '0.75em',
              opacity: 0.7,
            }}
            title="Opens in new tab"
          >
            ↗
          </span>
        )}
      </a>
    );
  },
  h1({ children }) {
    return (
      <h1
        style={{
          fontSize: '28px',
          fontWeight: 700,
          lineHeight: 1.2,
          color: 'var(--text-primary, #1e293b)',
          marginTop: '0',
          marginBottom: '8px',
        }}
      >
        {children}
      </h1>
    );
  },
  h2({ children }) {
    return (
      <h2
        style={{
          fontSize: '22px',
          fontWeight: 600,
          lineHeight: 1.3,
          color: 'var(--text-primary, #1e293b)',
          marginTop: '8px',
          marginBottom: '8px',
        }}
      >
        {children}
      </h2>
    );
  },
  h3({ children }) {
    return (
      <h3
        style={{
          fontSize: '18px',
          fontWeight: 600,
          lineHeight: 1.4,
          color: 'var(--text-primary, #1e293b)',
          marginTop: '8px',
          marginBottom: '8px',
        }}
      >
        {children}
      </h3>
    );
  },
  p({ children }) {
    return (
      <p
        style={{
          marginTop: '0',
          marginBottom: '16px',
          lineHeight: 1.6,
        }}
      >
        {children}
      </p>
    );
  },
  hr() {
    return (
      <hr
        style={{
          border: 'none',
          borderTop: '1px solid var(--border-default, #e2e8f0)',
          marginTop: '32px',
          marginBottom: '32px',
        }}
      />
    );
  },
  ul({ children }) {
    return (
      <ul
        style={{
          marginTop: '0',
          marginBottom: '16px',
          paddingLeft: '24px',
          lineHeight: 1.6,
        }}
      >
        {children}
      </ul>
    );
  },
  ol({ children }) {
    return (
      <ol
        style={{
          marginTop: '0',
          marginBottom: '16px',
          paddingLeft: '24px',
          lineHeight: 1.6,
        }}
      >
        {children}
      </ol>
    );
  },
  li({ children }) {
    return (
      <li
        style={{
          marginBottom: '4px',
        }}
      >
        {children}
      </li>
    );
  },
  table({ children }) {
    return (
      <table
        style={{
          borderCollapse: 'collapse',
          width: '100%',
          marginBottom: '1em',
        }}
      >
        {children}
      </table>
    );
  },
  thead({ children }) {
    return (
      <thead
        style={{
          backgroundColor: 'var(--bg-muted)',
        }}
      >
        {children}
      </thead>
    );
  },
  th({ children }) {
    return (
      <th
        style={{
          border: '1px solid var(--border-default, #e2e8f0)',
          padding: '8px 12px',
          textAlign: 'left',
          fontWeight: 600,
        }}
      >
        {children}
      </th>
    );
  },
  td({ children }) {
    return (
      <td
        style={{
          border: '1px solid var(--border-default, #e2e8f0)',
          padding: '8px 12px',
        }}
      >
        {children}
      </td>
    );
  },
  code({ className, children, ...props }) {
    const match = /language-(\w+)/.exec(className || '');
    const language = match ? match[1] : '';
    const codeContent = String(children).replace(/\n$/, '');

    // Render mermaid diagrams
    if (language === 'mermaid') {
      return <MermaidBlock chart={codeContent} />;
    }

    // Check if this is an inline code block (no language, single line, no block wrapper)
    const isInline = !className && !codeContent.includes('\n');

    if (isInline) {
      return (
        <code
          style={{
            backgroundColor: 'var(--bg-muted)',
            padding: '0.125em 0.25em',
            borderRadius: '0.25em',
            fontSize: '0.875em',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          }}
          {...props}
        >
          {children}
        </code>
      );
    }

    // Render regular code blocks with syntax highlighting placeholder
    return (
      <pre
        style={{
          backgroundColor: 'var(--bg-muted)',
          padding: '1em',
          borderRadius: '0.5em',
          overflow: 'auto',
          fontSize: '0.875em',
        }}
      >
        <code
          className={className}
          style={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          }}
          {...props}
        >
          {children}
        </code>
      </pre>
    );
  },
};

export function MarkdownPreview({ content, className }: MarkdownPreviewProps) {
  return (
    <div
      style={{
        fontSize: '14px',
        lineHeight: 1.6,
        color: 'var(--text-primary, #1e293b)',
      }}
      className={className}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </div>
  );
}
